import "../sellscreendialogs.scss";
import {
    type FC,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { useShopfrontDispatch, useShopfrontSelector } from "#/_Bootstrap/hooks.ts";
import { hideDialog } from "#/Actions/Dialogs/DialogActions.ts";
import {
    type GiftCardPayment,
    requestPayGiftCard,
    requestSelectGiftCard,
    type SaleAddPaymentOptions,
} from "#/Actions/SellScreen/SellScreenPaymentActions.tsx";
import { getPolicy } from "#/Utilities/Platform/Policies/Policies.ts";
import Button from "../../../../Common/Buttons/Button";
import StatusText from "../../../../Common/Forms/StatusText";
import { getExistingGiftCards } from "./RequestCode";

export const RequestGiftCard: FC<{
    method: string;
    amount: number;
    options: SaleAddPaymentOptions;
}> = ({
    method,
    amount,
    options,
}) => {
    const [ value, setValue ] = useState("");
    const [ found, setFound ] = useState<false | GiftCardPayment>(false);
    const [ multipleFound, setMultipleFound ] = useState<Array<GiftCardPayment>>([]);
    const [ source, setSource ] = useState("internal");
    const dispatch = useShopfrontDispatch();
    const inputRef = useRef<StatusText>(null);
    const submitRef = useRef(false);

    const outlet = useShopfrontSelector(state => state.location.outlet ? state.location.outlet.id : null);
    const payments = useShopfrontSelector(state => state.sellscreen.sale.payments);

    const saleCodes = useMemo(() => {
        return payments.map(payment => {
            if(payment.type !== "giftcard" || payment.status !== "completed" || payment.reverted) {
                return false;
            }

            try {
                const metaData = JSON.parse(payment.metadata);

                if(typeof metaData.gift_card !== "object" || metaData.gift_card === null) {
                    return false;
                }

                if(typeof metaData.gift_card.id === "string") {
                    return metaData.gift_card.id;
                }
            } catch {
                // Ignore
            }

            return false;
        })
            .filter(payment => !!payment) as Array<string>;
    }, [ payments ]);

    useEffect(() => {
        if(options.source === "auto" || options.source === "internal") {
            setSource("internal");
        } else {
            const sources = getPolicy("gift_card_sources", []);

            for(let i = 0, l = sources.length; i < l; i++) {
                if(sources[i].connection === options.source) {
                    setSource(sources[i].address);

                    return;
                }
            }

            throw new Error("Unable to find gift card source address");
        }
    }, [ options.source ]);

    const addCard = useCallback(() => {
        if(!found) {
            return;
        }

        if(multipleFound.length) {
            dispatch(requestSelectGiftCard(
                value,
                method,
                amount,
                options,
                multipleFound
            ));
        } else {
            dispatch(requestPayGiftCard(
                method,
                amount,
                {
                    ...options,
                    giftcard: found as GiftCardPayment,
                }
            ));
        }
    }, [ amount, dispatch, found, method, multipleFound, options, value ]);

    let multi = null;

    if(multipleFound.length) {
        multi = (
            <span className="multiple-gift-cards">Multiple gift cards found</span>
        );
    }

    return (
        <div className="request-gift-card">
            <p>Please enter or scan the gift card&apos;s code</p>
            <StatusText
                value={value}
                errorText="Gift Card not found"
                onChange={val => {
                    setValue(val);
                    setFound(false);
                    setMultipleFound([]);
                }}
                onKeyDown={e => {
                    if(e.key === "Enter") {
                        if(found) {
                            addCard();
                        } else {
                            submitRef.current = true;
                        }
                    }
                }}
                checkStatus={(code, callback) => {
                    return new Promise(res => {
                        return getExistingGiftCards(source, code, outlet, options.source as string, false)
                            .then(codes => codes.filter(code => !saleCodes.includes(code.id)))
                            .then(results => {
                                if(results.length > 1) {
                                    setMultipleFound(results);
                                }

                                if(results.length > 0) {
                                    setFound(results[0]);
                                }

                                res(!!results.length);
                            })
                            .catch(() => {
                                setFound(false);
                                res(false);
                            });
                    })
                        .then((results: boolean) => {
                            callback(results);

                            if(submitRef.current) {
                                if(results) {
                                    addCard();
                                } else {
                                    inputRef.current?.input.current?.select();
                                    submitRef.current = false;
                                }
                            }

                            return results;
                        });
                }}
                ref={inputRef}
                autoFocus
            />
            {multi}
            <div className="buttons">
                <Button
                    type="button"
                    classes={[ "button-cancel" ]}
                    onClick={() => dispatch(hideDialog())}
                >
                    Cancel
                </Button>
                <Button
                    type="button"
                    classes={[ "button-save" ]}
                    onClick={addCard}
                    disabled={!found}
                >
                    Add
                </Button>
            </div>
        </div>
    );
};
